import type { DocPageContent } from '../docs-content'

// order 1.5 coloca esto justo después de Primeros Pasos en el grupo Inicio.
const page = {
  order: 1.5,
  slug: 'concepts',
  title: 'Cómo funciona CachoCut',
  description:
    'El modelo conceptual: almacenamiento local, proyectos y medios vinculados, fotogramas y timecode, y el pipeline GPU.',
  category: 'Inicio',
  related: ['workspaces', 'getting-started', 'editing-tools'],
  sections: [
    {
      title: 'Local primero, por diseño',
      blocks: [
        {
          kind: 'list',
          items: [
            'CachoCut se ejecuta y almacena todo en una **carpeta de espacio de trabajo** en tu disco.',
            'Tu vídeo y audio originales están **vinculados**, no copiados ni subidos — CachoCut apunta a los archivos donde ya residen.',
            'Los proyectos, cachés, activos generados y exportaciones se escriben en el espacio de trabajo, por lo que puedes hacer copias de seguridad o moverlo como cualquier carpeta.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Dado que los medios están vinculados, mover o renombrar un archivo fuente aparece como **Medios Faltantes** hasta que lo vuelvas a vincular. Consulta [Espacios de Trabajo y Almacenamiento](workspaces).',
        },
      ],
    },
    {
      title: 'Proyectos, pistas y clips',
      blocks: [
        {
          kind: 'list',
          items: [
            'Un **proyecto** contiene una línea de tiempo, su configuración (resolución y frecuencia de fotogramas) y referencias a los medios que utilizas.',
            'La línea de tiempo apila **pistas**; las pistas superiores se renderizan sobre las inferiores.',
            'Un **clip** en la línea de tiempo es una ventana a un archivo fuente — recortar un clip cambia qué parte del archivo fuente se reproduce, no altera el archivo.',
            'Una **capa de ajuste** es un clip que no lleva medios propios — aplica sus efectos y color a cada clip que tiene debajo.',
          ],
        },
      ],
    },
    {
      title: 'Secuencias y clips compuestos',
      blocks: [
        {
          kind: 'paragraph',
          text: 'CachoCut tiene una primitiva para **una línea de tiempo dentro de tu proyecto** — una composición. Se presenta de dos formas según cómo la uses.',
        },
        {
          kind: 'list',
          items: [
            'Una **secuencia** es una línea de tiempo independiente en el mismo proyecto, que se abre desde una pestaña en la barra de pestañas. Cada secuencia tiene sus propias pistas, clips e historial de edición — la línea de tiempo principal es en sí misma una secuencia.',
            'Un **clip compuesto** toma una sección de la línea de tiempo y la agrupa en un único elemento de medios reutilizable. En la línea de tiempo se comporta como un clip — mover, recortar, añadir efectos — mientras que su contenido reside en su propia línea de tiempo.',
            'Ambos son la misma cosa vista de forma diferente: abre un clip compuesto para editar su contenido como una secuencia, y cualquier secuencia puede colocarse en otra línea de tiempo como un clip compuesto.',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: 'Una composición puede contener otras composiciones, pero nunca a sí misma — CachoCut bloquea referencias circulares, por lo que un clip compuesto nunca puede terminar anidado dentro de su propio contenido.',
        },
      ],
    },
    {
      title: 'Fotogramas y timecode',
      blocks: [
        {
          kind: 'list',
          items: [
            'La temporización se mide en **fotogramas**. Un clip tiene un fotograma de inicio y una duración en fotogramas a la frecuencia de fotogramas del proyecto.',
            'La vista previa muestra **timecode**; haz clic en el indicador en algunas vistas para cambiar a números de fotograma.',
            'Los medios fuente pueden tener una frecuencia de fotogramas diferente a la del proyecto, por lo que CachoCut convierte los fotogramas fuente a fotogramas de proyecto cuando editas.',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: 'Establece la resolución y la frecuencia de fotogramas del proyecto para que coincidan con tu destino de entrega cuando crees el proyecto.',
        },
      ],
    },
    {
      title: 'Todo se renderiza en tu máquina',
      blocks: [
        {
          kind: 'list',
          items: [
            'La reproducción, efectos, color y herramientas de IA se ejecutan localmente utilizando tu GPU (WebGPU) y CPU (WebCodecs).',
            'Los **proxies** son copias más ligeras de medios pesados que facilitan una edición fluida; la exportación final siempre utiliza los originales.',
            'Como no hay renderizado en la nube, mantener la pestaña abierta durante la exportación es importante — el renderizado ocurre directamente en CachoCut.',
          ],
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page